import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useDispatch } from 'react-redux';
import { doc, getDoc, updateDoc, addDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';

const PaymentSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    const finalizePayment = async () => {
      if (!sessionId) {
        toast.error('Invalid payment session');
        setError('Missing session ID');
        setLoading(false);
        return;
      }

      try {
        // Show processing toast
        toast.loading('Processing payment...', { id: 'payment-processing' });
        
        // Verify the payment session with your backend
        // Use the direct API URL instead of relying on the proxy
        const apiUrl = `http://localhost:5001/api/venice/verify-payment?session_id=${sessionId}`;
        console.log(`Verifying payment (attempt ${retryCount + 1}) at:`, apiUrl);
        
        const response = await fetch(apiUrl, {
          // Add cache control headers to avoid caching issues
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        });
        
        // Check if response is ok before trying to parse JSON
        if (!response.ok) {
          const errorText = await response.text();
          console.error('Server response error:', response.status, errorText);
          throw new Error(`Server error: ${response.status} - ${errorText}`);
        }
        
        // Parse JSON response
        const data = await response.json();
        console.log('Payment verification response:', data);
        
        toast.dismiss('payment-processing');
        
        if (!data.success) {
          throw new Error(data.error || 'Failed to verify payment');
        }

        const { type, userId, amount } = data.metadata;

        if (type === 'account_credit') {
          // Handle account credit addition
          const userRef = doc(db, "users", userId);
          const userDoc = await getDoc(userRef);
          
          if (!userDoc.exists()) {
            throw new Error('User not found');
          }

          const currentCredit = userDoc.data().accountCredit || 0;
          await updateDoc(userRef, {
            accountCredit: currentCredit + parseFloat(amount),
            lastUpdated: new Date().toISOString()
          });

          // Create credit addition record
          await addDoc(collection(db, "payments"), {
            userId,
            amount: parseFloat(amount),
            timestamp: new Date().toISOString(),
            status: 'completed',
            transactionId: sessionId,
            method: 'Credit Card',
            type: 'credit_addition'
          });

          toast.success('Credit added successfully!');
          setLoading(false);
          
          // Redirect to account settings after 3 seconds
          setTimeout(() => {
            navigate('/?tab=accountSettings');
          }, 3000);
        } else if (type === 'rental' || type === 'vcu_rental') {
          // Handle rental payment
          const { offerId, days, startDate, endDate } = data.metadata;

          if (!offerId || !days) {
            throw new Error('Missing rental data in payment metadata');
          }

          // Update the offer in Firestore
          const offerRef = doc(db, "offers", offerId);
          const offerDoc = await getDoc(offerRef);
          
          if (!offerDoc.exists()) {
            throw new Error('Offer not found');
          }

          // Get the user's email from Firestore (needed for proper "My Rentals" filtering)
          const userRef = doc(db, "users", userId);
          const userDoc = await getDoc(userRef);
          
          if (!userDoc.exists()) {
            console.warn('User document not found, using userId as consumer');
          }
          
          // Calculate dates if not provided in metadata
          const rentStartDate = startDate || new Date().toISOString();
          const rentEndDate = endDate || new Date(Date.now() + parseInt(days) * 24 * 60 * 60 * 1000).toISOString();

          // Set the consumer as the user's email if available, otherwise use userId
          const userEmail = userDoc.exists() ? userDoc.data().email : null;
          
          await updateDoc(offerRef, {
            status: 'locked',
            consumer: userEmail || userId, // Use email for consumer to match filtering in My Rentals
            consumerId: userId, // Also store the userId for reference
            startDate: rentStartDate,
            endDate: rentEndDate,
            lastUpdated: new Date().toISOString()
          });

          // Create payment record
          await addDoc(collection(db, "payments"), {
            offerId,
            userId,
            amount: parseFloat(amount),
            timestamp: new Date().toISOString(),
            status: 'completed',
            transactionId: sessionId,
            method: 'Credit Card',
            days: parseInt(days)
          });

          toast.success('Payment successful!');
          setLoading(false);

          // Manually verify that the rental was created correctly
          console.log(`Verifying rental for offer ${offerId} with consumer ${userEmail || userId}`);
          try {
            // Allow a small delay for Firestore to update
            setTimeout(async () => {
              const offerRef = doc(db, "offers", offerId);
              const updatedOfferDoc = await getDoc(offerRef);
              
              if (updatedOfferDoc.exists()) {
                const updatedData = updatedOfferDoc.data();
                console.log("Updated offer data:", {
                  status: updatedData.status,
                  consumer: updatedData.consumer, 
                  consumerId: updatedData.consumerId
                });
              }
            }, 1000);
          } catch (verifyError) {
            console.error("Error verifying rental update:", verifyError);
          }

          // Redirect to My Rentals after 3 seconds
          setTimeout(() => {
            navigate('/?tab=myRentals');
          }, 3000);
        } else {
          throw new Error(`Unknown payment type: ${type}`);
        }
      } catch (error: any) {
        console.error(`Error finalizing payment (attempt ${retryCount + 1}):`, error);
        
        // Retry logic - up to 3 retries with increasing delay
        if (retryCount < 2) {
          const retryDelay = (retryCount + 1) * 1500; // 1.5s, 3s
          toast.dismiss('payment-processing');
          toast.loading(`Retry attempt ${retryCount + 1}... (will retry in ${retryDelay/1000}s)`, { id: 'retry-toast' });
          
          setTimeout(() => {
            toast.dismiss('retry-toast');
            setRetryCount(prev => prev + 1);
          }, retryDelay);
          return;
        }
        
        // After all retries, show error
        toast.dismiss('payment-processing');
        toast.dismiss('retry-toast');
        toast.error(error.message || 'Error processing payment. Please contact support.');
        setError(error.message || 'Unknown error occurred');
        setLoading(false);
        
        // Log additional details for debugging
        console.error('Error details:', {
          message: error.message,
          stack: error.stack,
          sessionId
        });
      }
    };

    finalizePayment();
  }, [sessionId, navigate, dispatch, retryCount]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-100">
      <div className="max-w-md w-full mx-auto bg-white rounded-lg shadow-md p-8 text-center">
        {!error ? (
          <>
            <div className="mb-6">
              <svg 
                className="w-16 h-16 text-green-500 mx-auto" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth="2" 
                  d="M5 13l4 4L19 7"
                ></path>
              </svg>
            </div>
            
            <h2 className="text-2xl font-bold text-gray-800 mb-3">Payment Successful!</h2>
            
            <p className="text-gray-600 mb-6">
              Thank you for your payment. Your transaction has been completed.
            </p>
          </>
        ) : (
          <>
            <div className="mb-6">
              <svg 
                className="w-16 h-16 text-red-500 mx-auto" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth="2" 
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                ></path>
              </svg>
            </div>
            
            <h2 className="text-2xl font-bold text-gray-800 mb-3">Payment Error</h2>
            
            <p className="text-gray-600 mb-6">
              There was a problem processing your payment: {error}
            </p>
            <p className="text-sm text-gray-500 mb-4">
              Session ID: {sessionId}
            </p>
          </>
        )}

        {loading ? (
          <div className="animate-pulse">
            <p className="text-blue-500">
              {retryCount > 0 ? `Finalizing payment (attempt ${retryCount + 1})...` : 'Finalizing your payment...'}
            </p>
          </div>
        ) : (
          <>
            <p className="text-gray-600 mb-6">
              {!error ? 'You will be redirected shortly.' : 'Please try again or contact support.'}
            </p>
            <button 
              onClick={() => navigate('/')}
              className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded-md transition-colors"
            >
              Return to Dashboard
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess; 